import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Receipt, FileText, X as XIcon, Plus, Download, BookOpen, HelpCircle, ChevronDown, ChevronUp, PlayCircle, RotateCcw } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type { InvoiceDocument, InvoiceStatus, InvoiceType } from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import PageHeader from '../ui/PageHeader';
import StatusPill from '../ui/StatusPill';
import EmptyState from '../ui/EmptyState';
import { fmtDateDe, fmtMoney, statusLabel, statusTone, typeLabel } from './invoiceHelpers';
import BelegOnboarding from '../belegOnboarding/BelegOnboarding';

const HELP_BANNER_KEY = 'seadent-belege-help-banner-v1';

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

type TabKey = 'all' | 'offer' | 'invoice' | 'cancellation';

const TAB_DEFS: { key: TabKey; label: string; type?: InvoiceType }[] = [
    { key: 'all', label: 'Alle' },
    { key: 'offer', label: 'Angebote', type: 'offer' },
    { key: 'invoice', label: 'Rechnungen', type: 'invoice' },
    { key: 'cancellation', label: 'Storno', type: 'cancellation' },
];

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
    <th
        scope="col"
        className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted ${align === 'right' ? 'text-right' : 'text-left'}`}
    >
        {children}
    </th>
);

const InvoiceListPage: React.FC = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();

    const [tab, setTab] = useState<TabKey>('all');
    const [docs, setDocs] = useState<InvoiceDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const [tourOpen, setTourOpen] = useState(false);
    const [tourChapter, setTourChapter] = useState<'angebot' | 'rechnung' | 'storno' | undefined>(undefined);
    const [helpExpanded, setHelpExpanded] = useState<boolean>(() => {
        try { return localStorage.getItem(HELP_BANNER_KEY) !== 'collapsed'; } catch { return true; }
    });
    const toggleHelp = () => {
        setHelpExpanded(prev => {
            const next = !prev;
            try { localStorage.setItem(HELP_BANNER_KEY, next ? 'expanded' : 'collapsed'); } catch {}
            return next;
        });
    };
    const startTour = (chapter?: 'angebot' | 'rechnung' | 'storno') => {
        setTourChapter(chapter);
        setTourOpen(true);
    };

    const load = async () => {
        try {
            setLoading(true);
            setError(null);
            const type = TAB_DEFS.find(t => t.key === tab)?.type;
            const res = await apiService.listInvoices({
                type,
                status: statusFilter || undefined,
                q: search || undefined,
                from: from || undefined,
                to: to || undefined,
            });
            setDocs(res.data.data || []);
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Belege konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handle = setTimeout(load, search ? 250 : 0);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, statusFilter, from, to, search]);

    const hasActiveFilters = !!(search || statusFilter || from || to);
    const resetFilters = () => { setSearch(''); setStatusFilter(''); setFrom(''); setTo(''); };

    const stats = useMemo(() => {
        const open = docs.filter(d => d.status === 'issued').length;
        const overdue = docs.filter(d => d.status === 'issued' && d.due_date && d.due_date < new Date().toISOString().slice(0, 10)).length;
        const sum = docs.reduce((s, d) => s + (d.total_cents || 0), 0);
        return { total: docs.length, open, overdue, sum };
    }, [docs]);

    const startNew = (type: InvoiceType) => navigate(`/admin/invoices/new?type=${type}`);

    const downloadPdf = async (doc: InvoiceDocument) => {
        try {
            const blob = await apiService.fetchInvoicePdf(doc.id, { preview: !doc.finalized_at });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'PDF konnte nicht geladen werden', 'error');
        }
    };

    return (
        <div>
            <PageHeader
                eyebrow="Abrechnung"
                title="Belege"
                description="Angebote, Rechnungen und Stornobelege — fortlaufend nummeriert nach Festschreibung"
                actions={
                    <>
                        <span className="text-xs text-brand-text-muted">
                            <span className="font-semibold text-brand-text tabular-nums">{stats.total}</span> Belege
                            {stats.open > 0 && <> · <span className="tabular-nums">{stats.open}</span> offen</>}
                            {stats.overdue > 0 && <> · <span className="text-rose-600 tabular-nums">{stats.overdue}</span> überfällig</>}
                        </span>
                        <Link
                            to="/admin/invoices/catalog"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                            title="Leistungskatalog verwalten"
                        >
                            <BookOpen className="h-3.5 w-3.5" strokeWidth={1.75} />
                            Katalog
                        </Link>
                        <button
                            type="button"
                            onClick={() => startNew('offer')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-primary border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                        >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                            Angebot
                        </button>
                        <button
                            type="button"
                            onClick={() => startNew('invoice')}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer"
                        >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                            Rechnung
                        </button>
                    </>
                }
            />

            {/* Hilfe-Banner: aufklappbare Onboarding-Box */}
            <div className="mb-4 rounded-xl border border-brand-accent/30 bg-gradient-to-br from-brand-accent/5 to-brand-accent/10 overflow-hidden">
                <button
                    type="button"
                    onClick={toggleHelp}
                    aria-expanded={helpExpanded}
                    aria-controls="belege-help-body"
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-brand-accent/5 transition-colors cursor-pointer"
                >
                    <div className="flex items-center gap-2.5 min-w-0">
                        <span className="h-7 w-7 rounded-md bg-brand-primary text-white flex items-center justify-center shrink-0">
                            <HelpCircle className="w-4 h-4" strokeWidth={1.75} />
                        </span>
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-brand-text">Wie funktionieren Belege?</div>
                            <div className="text-xs text-brand-text-muted truncate">
                                Angebot, Rechnung und Stornorechnung — kurz erklärt, mit interaktiver Tour.
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); startTour(); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); startTour(); } }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer"
                        >
                            <PlayCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Tour starten
                        </span>
                        {helpExpanded
                            ? <ChevronUp className="w-4 h-4 text-brand-text-muted" strokeWidth={1.75} />
                            : <ChevronDown className="w-4 h-4 text-brand-text-muted" strokeWidth={1.75} />}
                    </div>
                </button>

                {helpExpanded && (
                    <div id="belege-help-body" className="px-4 pb-4 pt-1 border-t border-brand-accent/20">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                            <button
                                type="button"
                                onClick={() => startTour('angebot')}
                                className="text-left rounded-lg border border-brand-border bg-brand-surface hover:border-brand-primary/50 hover:shadow-sm transition-all p-3 cursor-pointer group"
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="h-7 w-7 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center">
                                        <FileText className="w-4 h-4" strokeWidth={1.75} />
                                    </span>
                                    <span className="text-xs font-mono font-semibold text-brand-text-muted">AN-…</span>
                                    <span className="ml-auto text-[10px] text-brand-text-muted">~2 min</span>
                                </div>
                                <div className="text-sm font-semibold text-brand-text mb-0.5">Angebot</div>
                                <div className="text-xs text-brand-text-muted leading-snug">
                                    Kostenvoranschlag vor der Behandlung — vom Patienten annehmen lassen, dann in eine Rechnung umwandeln.
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => startTour('rechnung')}
                                className="text-left rounded-lg border border-brand-border bg-brand-surface hover:border-brand-primary/50 hover:shadow-sm transition-all p-3 cursor-pointer group"
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="h-7 w-7 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center">
                                        <Receipt className="w-4 h-4" strokeWidth={1.75} />
                                    </span>
                                    <span className="text-xs font-mono font-semibold text-brand-text-muted">RE-…</span>
                                    <span className="ml-auto text-[10px] text-brand-text-muted">~2 min</span>
                                </div>
                                <div className="text-sm font-semibold text-brand-text mb-0.5">Rechnung</div>
                                <div className="text-xs text-brand-text-muted leading-snug">
                                    Hauptpfad: Termin abschließen, Quick-Chips klicken, ausstellen und per Mail senden.
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => startTour('storno')}
                                className="text-left rounded-lg border border-brand-border bg-brand-surface hover:border-brand-primary/50 hover:shadow-sm transition-all p-3 cursor-pointer group"
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="h-7 w-7 rounded-md bg-rose-50 text-rose-700 flex items-center justify-center">
                                        <RotateCcw className="w-4 h-4" strokeWidth={1.75} />
                                    </span>
                                    <span className="text-xs font-mono font-semibold text-brand-text-muted">ST-…</span>
                                    <span className="ml-auto text-[10px] text-brand-text-muted">~2 min</span>
                                </div>
                                <div className="text-sm font-semibold text-brand-text mb-0.5">Stornorechnung</div>
                                <div className="text-xs text-brand-text-muted leading-snug">
                                    Korrekturen an festgeschriebenen Rechnungen — Pflicht-Workflow fürs Finanzamt.
                                </div>
                            </button>
                        </div>
                        <div className="mt-3 text-[11px] text-brand-text-muted">
                            Tipp: Die Tour kannst du jederzeit über <strong>Tour starten</strong> oben rechts erneut öffnen.
                        </div>
                    </div>
                )}
            </div>

            {error && (
                <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
                    {error}
                </div>
            )}

            <BelegOnboarding
                open={tourOpen}
                onClose={() => setTourOpen(false)}
                initialChapter={tourChapter}
            />

            {/* Tabs */}
            <div role="tablist" aria-label="Belegtypen" className="mb-4 flex items-center gap-1 border-b border-brand-border overflow-x-auto">
                {TAB_DEFS.map(t => (
                    <button
                        key={t.key}
                        role="tab"
                        aria-selected={tab === t.key}
                        onClick={() => setTab(t.key)}
                        className={`relative px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            tab === t.key
                                ? 'text-brand-primary'
                                : 'text-brand-text-muted hover:text-brand-text'
                        }`}
                    >
                        {t.label}
                        {tab === t.key && (
                            <span aria-hidden className="absolute inset-x-2 -bottom-px h-0.5 bg-brand-primary rounded-full" />
                        )}
                    </button>
                ))}
            </div>

            {/* Filter card */}
            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div className="lg:col-span-2">
                        <label className={labelClass}>Suche (Nummer, Patient, Stadt)</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none" strokeWidth={1.75} aria-hidden />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="z. B. RE-0042, Müller, Palma"
                                className={`${inputClass} pl-9`}
                            />
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>Status</label>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus | '')} className={inputClass}>
                            <option value="">Alle</option>
                            <option value="draft">Entwurf</option>
                            <option value="sent">Versendet</option>
                            <option value="accepted">Angenommen</option>
                            <option value="rejected">Abgelehnt</option>
                            <option value="issued">Ausgestellt</option>
                            <option value="paid">Bezahlt</option>
                            <option value="canceled">Storniert</option>
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className={labelClass}>Von</label>
                            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>Bis</label>
                            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} />
                        </div>
                    </div>
                </div>
                {hasActiveFilters && (
                    <div className="mt-3 flex justify-end">
                        <button
                            onClick={resetFilters}
                            className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer"
                        >
                            <XIcon className="h-3 w-3" strokeWidth={2} />
                            Filter zurücksetzen
                        </button>
                    </div>
                )}
            </div>

            {/* List */}
            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-brand-text-muted">Lade…</div>
                ) : docs.length === 0 ? (
                    <EmptyState
                        icon={Receipt}
                        title={hasActiveFilters ? 'Keine Treffer' : 'Noch keine Belege'}
                        description={
                            hasActiveFilters
                                ? 'Keine Belege entsprechen den aktuellen Filtern.'
                                : 'Belege werden meist direkt aus einem Termin oder der Patientenakte erstellt — alternativ rechts oben anlegen.'
                        }
                    />
                ) : (
                    <>
                        {/* Desktop table */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-brand-border">
                                <thead className="bg-brand-surface-2/60">
                                    <tr>
                                        <Th>Nr. / Typ</Th>
                                        <Th>Patient</Th>
                                        <Th>Datum</Th>
                                        <Th>Status</Th>
                                        <Th align="right">Betrag</Th>
                                        <Th align="right">Aktion</Th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-brand-border">
                                    {docs.map((d) => (
                                        <tr
                                            key={d.id}
                                            className="hover:bg-brand-surface-2/60 transition-colors cursor-pointer"
                                            onClick={() => navigate(`/admin/invoices/${d.id}`)}
                                        >
                                            <td className="px-4 py-3 text-sm">
                                                <div className="font-medium text-brand-text tabular-nums">
                                                    {d.number || <span className="italic text-brand-text-muted">Entwurf</span>}
                                                </div>
                                                <div className="text-xs text-brand-text-muted">{typeLabel(d.type)}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="text-brand-text truncate max-w-[240px]">
                                                    {d.billing_address?.name || '—'}
                                                </div>
                                                <div className="text-xs text-brand-text-muted tabular-nums">
                                                    {d.patient_number}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="text-brand-text tabular-nums">{fmtDateDe(d.issue_date)}</div>
                                                {d.type === 'invoice' && d.due_date && (
                                                    <div className="text-xs text-brand-text-muted tabular-nums">
                                                        fällig {fmtDateDe(d.due_date)}
                                                    </div>
                                                )}
                                                {d.type === 'offer' && d.valid_until && (
                                                    <div className="text-xs text-brand-text-muted tabular-nums">
                                                        gültig bis {fmtDateDe(d.valid_until)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <StatusPill tone={statusTone(d.status)} dot>
                                                    {statusLabel(d.status)}
                                                </StatusPill>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right">
                                                <div className="font-medium text-brand-text tabular-nums">
                                                    {fmtMoney(d.total_cents, d.currency || 'EUR')}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right">
                                                <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                    {(d.finalized_at || d.status === 'draft') && (
                                                        <button
                                                            type="button"
                                                            onClick={() => downloadPdf(d)}
                                                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-primary hover:bg-brand-accent/10 rounded transition-colors cursor-pointer"
                                                            title="PDF öffnen"
                                                        >
                                                            <Download className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/admin/invoices/${d.id}`)}
                                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-brand-primary hover:bg-brand-accent/10 rounded transition-colors cursor-pointer"
                                                    >
                                                        Öffnen →
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile cards */}
                        <ul className="md:hidden divide-y divide-brand-border">
                            {docs.map((d) => (
                                <li key={d.id}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/admin/invoices/${d.id}`)}
                                        className="w-full text-left block p-4 hover:bg-brand-surface-2/60 transition-colors cursor-pointer"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="font-medium text-brand-text tabular-nums">
                                                    {d.number || <span className="italic text-brand-text-muted">Entwurf</span>}
                                                </div>
                                                <div className="text-xs text-brand-text-muted">{typeLabel(d.type)} · {d.billing_address?.name || '—'}</div>
                                                <div className="text-xs text-brand-text-muted tabular-nums">{fmtDateDe(d.issue_date)}</div>
                                            </div>
                                            <div className="flex flex-col items-end gap-1">
                                                <StatusPill tone={statusTone(d.status)} dot>
                                                    {statusLabel(d.status)}
                                                </StatusPill>
                                                <div className="font-medium text-brand-text tabular-nums text-sm">
                                                    {fmtMoney(d.total_cents, d.currency || 'EUR')}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>

            {/* Summary footer */}
            {docs.length > 0 && (
                <div className="mt-4 flex flex-col sm:flex-row items-end sm:items-center justify-between gap-2 text-xs text-brand-text-muted">
                    <div className="inline-flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5" strokeWidth={1.75} />
                        <span>{docs.length} Belege im aktuellen Filter</span>
                    </div>
                    <div className="text-sm">
                        Summe: <span className="font-semibold text-brand-text tabular-nums">{fmtMoney(stats.sum)}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvoiceListPage;
